
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

type Status = '已完成' | '進行中';

function StatusBadge({ status }: { status: Status }) {
  if (status === '已完成') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full border border-green-200">
        <i className="ri-checkbox-circle-fill text-green-500 text-sm"></i>
        已完成
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full border border-orange-200">
      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></span>
      進行中
    </span>
  );
}

export default function HomeProjects() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const featured = [
    {
      title: '智慧法律案件管理系統',
      description: '結合法律實務背景，自主開發微服務架構的法律案件管理平台，整合身份驗證、案件追蹤、文件管理與通知系統，部署於 Kubernetes 容器環境。',
      status: '進行中' as Status,
      tags: ['Microservices', 'Docker', 'Kubernetes'],
      icon: 'ri-scales-3-line',
      color: 'from-rose-500 to-orange-500',
    },
    {
      title: 'Tableau 物流追蹤數據分析',
      description: '參與物流追蹤數據分析專案，擔任資料視覺化組成員，以 Tableau 建立互動式 Dashboard，呈現配送效率、延誤分析與路線優化洞察。',
      status: '已完成' as Status,
      tags: ['Tableau', 'Data Visualization', '物流分析'],
      icon: 'ri-truck-line',
      color: 'from-amber-400 to-orange-500',
      link: 'https://bit.ly/public_tableau_francesshen',
    },
    {
      title: '台北房產 AI 分析系統',
      description: '擔任 AI 工程師，參與台北房地產分析系統開發，以機器學習模型預測房價趨勢，並提供視覺化查詢介面。',
      status: '已完成' as Status,
      tags: ['AI', 'Python', '房地產', 'Machine Learning'],
      icon: 'ri-building-2-line',
      color: 'from-orange-500 to-rose-400',
      link: 'http://tperealestate.franceslaw.tech/',
    },
  ];

  return (
    <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-orange-50 to-rose-50" ref={sectionRef}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between mb-8 md:mb-14 gap-4">
          <div>
            <div
              className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <span className="text-sm font-semibold text-orange-700">個人專案</span>
            </div>
            <h2
              className={`text-3xl md:text-5xl font-bold text-gray-900 leading-tight transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
            >
              精選<br />個人專案
            </h2>
          </div>
          <Link
            to="/side-projects"
            className={`flex items-center gap-2 px-6 py-3 rounded-full border-2 border-orange-500 text-orange-600 font-semibold text-sm hover:bg-orange-500 hover:text-white transition-all duration-300 whitespace-nowrap mb-2 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ transitionDelay: '200ms' }}
          >
            查看全部專案
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((project, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-500 overflow-hidden flex flex-col ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${300 + index * 120}ms` }}
            >
              {/* Status badge — top right */}
              <div className="absolute top-4 right-4 z-10">
                <StatusBadge status={project.status} />
              </div>

              {/* Color bar */}
              <div className={`h-1.5 w-full bg-gradient-to-r ${project.color}`}></div>

              <div className="p-7 flex flex-col flex-grow">
                {/* Icon + Title */}
                <div className="flex items-start gap-4 mb-4 pr-20">
                  <div className={`w-12 h-12 bg-gradient-to-br ${project.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <i className={`${project.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {project.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-grow">
                  {project.description}
                </p>

                {/* Tags + Link */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, i) => (
                      <span key={i} className="px-2.5 py-1 bg-orange-50 text-orange-700 text-xs font-semibold rounded-lg border border-orange-100">
                        {tag}
                      </span>
                    ))}
                  </div>
                  {'link' in project && project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors duration-200 flex-shrink-0"
                    >
                      <i className="ri-external-link-line text-sm"></i>
                      開啟連結
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '700ms' }}
        >
          <Link
            to="/side-projects"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-semibold text-base bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            查看所有專案
            <i className="ri-arrow-right-line text-lg"></i>
          </Link>
        </div>

      </div>
    </section>
  );
}
